// src/pages/services/AppDevelopment.jsx

import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ServiceLayout from "../../components/ServiceLayout";
import ServiceFeatureCard from "../../components/ServiceFeatureCard";

import img1 from "../../assets/app1.jpg";
import img2 from "../../assets/app2.jpg";
import img3 from "../../assets/app3.jpg";
import img4 from "../../assets/app4.jpg";
import img5 from "../../assets/app5.jpg";
import img6 from "../../assets/app6.png";
import bannerImg from "../../assets/ser5.jpg";
import overviewImg from "../../assets/app.jpg";

export default function AppDevelopment() {
  const scrollRef = useRef(null);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const amount = 320;
      scrollRef.current.scrollBy({
        left: direction === "left" ? -amount : amount,
        behavior: "smooth",
      });
    }
  };

  const capabilities = [
    {
      title: "Cross-Platform & Native Development",
      description: "Code once, deploy anywhere — or go native for peak performance on iOS and Android.",
    },
    {
      title: "Custom Features & Integrations",
      description: "We tailor app functionalities to match your workflow, goals, and third-party integrations.",
    },
    {
      title: "Intuitive UI/UX Design",
      description: "A delightful user experience built with clean design, easy navigation, and responsiveness in mind.",
    },
    {
      title: "Ongoing Support & Maintenance",
      description: "Keep your app up to date, secure, and functioning flawlessly with our support packages.",
    },
  ];

  const services = [
    {
      title: "iOS & Android App Development",
      image: img1,
      description: "Native mobile applications for iOS and Android, ensuring seamless platform-specific experiences.",
    },
    {
      title: "Cross-Platform App Development",
      image: img2,
      description: "Reach more users with Flutter or React Native apps using a single codebase across platforms.",
    },
    {
      title: "E-commerce App Development",
      image: img3,
      description: "Build feature-rich mobile commerce apps with payments, listings, user profiles, and more.",
    },
    {
      title: "Custom Mobile App Development",
      image: img4,
      description: "Tailor-made apps from the ground up to fit your unique business needs and workflows.",
    },
    {
      title: "UI/UX Design for Mobile Apps",
      image: img5,
      description: "Beautiful and intuitive app interfaces focused on usability and user satisfaction.",
    },
    {
      title: "App Maintenance & Support",
      image: img6,
      description: "Ongoing updates, fixes, and technical support to keep your app running smoothly.",
    },
  ];

  return (
    <ServiceLayout
      title="App Development Services"
      bannerImg={bannerImg}
      overviewImg={overviewImg}
      overviewHeading="Custom Mobile Apps for Every Business Need"
      overviewText="We specialize in crafting scalable, feature-rich, and visually stunning mobile applications that empower businesses to connect with users more effectively."
      capabilities={capabilities}
      buttonText="Build Your Custom App"
      servicesHeading="Our App Development Services"
    >
      <div className="relative px-4 sm:px-6 pb-16">
        <div className="relative flex items-center">
          {/* Scroll Left */}
          <button
            onClick={() => scroll("left")}
            className="hidden sm:block absolute left-4 z-10 bg-white border rounded-full p-2 shadow-md hover:bg-gray-100"
            style={{ top: "35%", transform: "translateY(-50%)" }}
          >
            <ChevronLeft size={40} />
          </button>

          {/* Cards Scroll Area */}
          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto scroll-smooth px-4 sm:px-12 scrollbar-hide w-full"
            style={{ scrollBehavior: "smooth" }}
          >
            {services.map((service, idx) => (
              <div
                key={idx}
                className="min-w-[300px] max-w-[300px] h-[250px] flex-shrink-0 transform transition-transform duration-300 hover:scale-105"
              >
                <ServiceFeatureCard
                  title={service.title}
                  image={service.image}
                  description={service.description}
                  link="/contact"
                />
              </div>
            ))}
          </div>

          {/* Scroll Right */}
          <button
            onClick={() => scroll("right")}
            className="hidden sm:block absolute right-4 z-10 bg-white border rounded-full p-2 shadow-md hover:bg-gray-100"
            style={{ top: "35%", transform: "translateY(-50%)" }}
          >
            <ChevronRight size={40} />
          </button>
        </div>
      </div>
    </ServiceLayout>
  );
}
